import maplibregl from "maplibre-gl";

import { type VehicleManager } from "../../../../managers/VehicleManager";
import { type CompactShape } from "../../../../model/realtime/CompactShape";
import { type RealtimeCache } from "../../../../model/RealtimeCache";
import type { TripDelayWire } from "../../../../types/dtos/realtime-api/realtime.dto";
import {
    REALTIME_TRANSIT_PATH_CURRENT_LAYER_ID,
    REALTIME_TRANSIT_PATH_CURRENT_SOURCE_ID,
    REALTIME_TRANSIT_PATH_REMAINING_LAYER_ID,
    REALTIME_TRANSIT_PATH_REMAINING_SOURCE_ID,
    REALTIME_TRANSIT_PATH_TRAVERSED_LAYER_ID,
    REALTIME_TRANSIT_PATH_TRAVERSED_SOURCE_ID,
    VEHICLE_CUSTOM_LAYER_ID,
} from "../../mapLayerIds";
import {
    removeLayerAndSource,
    shapeViewToCoordinates,
    updateGeoJsonLineData,
} from "../routeLines";
import { TransitPathStopCollisionController } from "../stopOverlay";
import {
    buildRealtimeTransitPathMarkerPresentationFingerprint,
    REALTIME_TRANSIT_PATH_LINE_STYLE_ACTIVE,
    REALTIME_TRANSIT_PATH_LINE_STYLE_TRAVERSED,
    REALTIME_TRANSIT_PATH_SHAPE_SNAP_BACKWARD_TOLERANCE_POINTS,
} from "./realtimeTransitPath.configuration";
import { buildRealtimeTransitPathStopCollisionEntries } from "./realtimeTransitPath.mapper";
import {
    buildRealtimeTransitStaticStateFingerprint,
    getServiceDaySecondsForSchedule,
    resolveArrivalBasedNextStopIndex,
} from "./realtimeTransitPath.schedule.tools";
import {
    findNearestShapeIndex,
    resolveEffectiveNextStopIndex,
    resolveVehicleLeadingCoord,
} from "./realtimeTransitPath.shape.tools";

/** Mutable refs shared between static sync and segment refresh. */
export interface RealtimeTransitPathControllerRefs {
    readonly "stopLabelControllerRef":        React.MutableRefObject<TransitPathStopCollisionController | null>;
    readonly "prevNextStopIdxRef":            React.MutableRefObject<number>;
    readonly "lastStaticStateFingerprintRef": React.MutableRefObject<string>;
    readonly "shapePointsRef":                React.MutableRefObject<CompactShape | null>;
    readonly "shapeStartIdxRef":              React.MutableRefObject<number>;
    readonly "nextStopIndexRef":              React.MutableRefObject<number>;
    readonly "nextStopShapeIdxRef":           React.MutableRefObject<number>;
    readonly "previousStopShapeIdxRef":       React.MutableRefObject<number>;
    readonly "tripIdRef":                     React.MutableRefObject<string>;
    readonly "colorHexRef":                   React.MutableRefObject<string>;
}

/** Context for one realtime transit path sync pass. */
export interface RealtimeTransitPathSyncContext extends RealtimeTransitPathControllerRefs {
    readonly "map":            maplibregl.Map;
    readonly "tripId":         string;
    readonly "realtimeCache":  RealtimeCache;
    readonly "vehicleManager": VehicleManager;
    readonly "colorHex":       string;
    readonly "isDarkUi":       boolean;
}

/**
 * Removes all realtime transit path layers, sources, and stop label markers.
 *
 * @param map MapLibre map instance.
 * @param stopLabelControllerRef Collision controller ref.
 */
export function cleanupRealtimeTransitPath(
    map: maplibregl.Map,
    stopLabelControllerRef: React.MutableRefObject<TransitPathStopCollisionController | null>,
): void {
    removeLayerAndSource( map, REALTIME_TRANSIT_PATH_TRAVERSED_LAYER_ID, REALTIME_TRANSIT_PATH_TRAVERSED_SOURCE_ID );
    removeLayerAndSource( map, REALTIME_TRANSIT_PATH_CURRENT_LAYER_ID, REALTIME_TRANSIT_PATH_CURRENT_SOURCE_ID );
    removeLayerAndSource( map, REALTIME_TRANSIT_PATH_REMAINING_LAYER_ID, REALTIME_TRANSIT_PATH_REMAINING_SOURCE_ID );
    stopLabelControllerRef.current?.destroy();
    stopLabelControllerRef.current = null;
}

/**
 * Ensures a collision controller exists on the map.
 *
 * @param map MapLibre map instance.
 * @param stopLabelControllerRef Controller ref owned by the hook.
 * @returns Active collision controller instance.
 */
function ensureStopCollisionController(
    map: maplibregl.Map,
    stopLabelControllerRef: React.MutableRefObject<TransitPathStopCollisionController | null>,
): TransitPathStopCollisionController {
    if ( stopLabelControllerRef.current === null ) {
        stopLabelControllerRef.current = new TransitPathStopCollisionController( map );
    }

    return stopLabelControllerRef.current;
}

/**
 * Syncs past and remaining stop dots/labels through the collision controller.
 */
function syncRealtimeTransitPathStopLabels(
    map: maplibregl.Map,
    stopLabelControllerRef: React.MutableRefObject<TransitPathStopCollisionController | null>,
    pastStops: Parameters<typeof buildRealtimeTransitPathStopCollisionEntries>[0],
    remainingStops: Parameters<typeof buildRealtimeTransitPathStopCollisionEntries>[1],
    allShapePoints: CompactShape,
    colorHex: string,
    isDarkUi: boolean,
    delay: TripDelayWire | undefined,
): void {
    const controller = ensureStopCollisionController( map, stopLabelControllerRef );
    const entries = buildRealtimeTransitPathStopCollisionEntries(
        pastStops,
        remainingStops,
        allShapePoints,
        colorHex,
        isDarkUi,
        delay,
    );
    controller.setEntries( entries, buildRealtimeTransitPathMarkerPresentationFingerprint() );
}

/**
 * Updates remaining polyline + markers when next-stop index or visual state changes.
 *
 * @param ctx Mutable refs and map handles.
 * @returns Whether the trip still has a drawable next stop for today.
 */
export function trySyncRealtimeTransitPathStatic( ctx: RealtimeTransitPathSyncContext ): boolean {
    const schedule = ctx.realtimeCache.schedules.get( ctx.tripId );
    const delay = ctx.realtimeCache.delays.get( ctx.tripId );

    if ( schedule === undefined ) {
        cleanupRealtimeTransitPath( ctx.map, ctx.stopLabelControllerRef );
        ctx.prevNextStopIdxRef.current = -1;
        ctx.lastStaticStateFingerprintRef.current = "";
        ctx.shapePointsRef.current = null;

        return false;
    }

    const shapeEntry = ctx.realtimeCache.shapes.get( schedule.data.shape_id );
    if ( shapeEntry === undefined ) {
        cleanupRealtimeTransitPath( ctx.map, ctx.stopLabelControllerRef );
        ctx.prevNextStopIdxRef.current = -1;
        ctx.lastStaticStateFingerprintRef.current = "";
        ctx.shapePointsRef.current = null;

        return false;
    }

    const shapePoints = shapeEntry.data;
    const stops = schedule.data.stops;
    const nowSeconds = getServiceDaySecondsForSchedule( stops );

    const delayLookup = delay !== undefined ? new Map<number, number>( delay.stops ) : undefined;
    const getDelayForStopSequence = ( stopSeq: number ): number => {
        if ( delay === undefined || delayLookup === undefined ) {
            return 0;
        }

        const stopDelay = delayLookup.get( stopSeq );

        return stopDelay !== undefined ? stopDelay : delay.global;
    };

    const nextStopIndex = resolveArrivalBasedNextStopIndex(
        stops,
        getDelayForStopSequence,
        nowSeconds,
    );

    if ( nextStopIndex === -1 ) {
        cleanupRealtimeTransitPath( ctx.map, ctx.stopLabelControllerRef );
        ctx.prevNextStopIdxRef.current = -1;
        ctx.lastStaticStateFingerprintRef.current = "";
        ctx.shapePointsRef.current = null;

        return false;
    }

    const nextStopShapeIdx = stops[nextStopIndex].shape_idx;
    const previousStopShapeIdx = nextStopIndex > 0
        ? stops[nextStopIndex - 1].shape_idx
        : schedule.data.shape_start_idx;
    const effectiveNextStopIndex = resolveEffectiveNextStopIndex(
        stops,
        shapePoints,
        nextStopIndex,
        previousStopShapeIdx,
        nextStopShapeIdx,
        ctx.vehicleManager,
        ctx.tripId,
    );

    ctx.shapePointsRef.current = shapePoints;
    ctx.shapeStartIdxRef.current = schedule.data.shape_start_idx;
    ctx.nextStopIndexRef.current = effectiveNextStopIndex;
    ctx.nextStopShapeIdxRef.current = nextStopShapeIdx;
    ctx.previousStopShapeIdxRef.current = previousStopShapeIdx;
    ctx.tripIdRef.current = ctx.tripId;
    ctx.colorHexRef.current = ctx.colorHex;

    const stateFp = buildRealtimeTransitStaticStateFingerprint( delay, ctx.colorHex, ctx.isDarkUi );

    const pastStops = stops.slice( 0, effectiveNextStopIndex );
    const remainingStops = stops.slice( effectiveNextStopIndex );
    const nextStopIndexChanged = effectiveNextStopIndex !== ctx.prevNextStopIdxRef.current;
    const staticStateChanged = stateFp !== ctx.lastStaticStateFingerprintRef.current;

    if ( nextStopIndexChanged ) {
        removeLayerAndSource(
            ctx.map,
            REALTIME_TRANSIT_PATH_REMAINING_LAYER_ID,
            REALTIME_TRANSIT_PATH_REMAINING_SOURCE_ID,
        );

        const remainingView = shapePoints.slice( nextStopShapeIdx, shapePoints.length - 1 );
        const remainingCoords = shapeViewToCoordinates( remainingView );
        updateGeoJsonLineData(
            ctx.map,
            REALTIME_TRANSIT_PATH_REMAINING_SOURCE_ID,
            REALTIME_TRANSIT_PATH_REMAINING_LAYER_ID,
            remainingCoords,
            ctx.colorHex,
            REALTIME_TRANSIT_PATH_LINE_STYLE_ACTIVE,
            REALTIME_TRANSIT_PATH_CURRENT_LAYER_ID,
        );

        ctx.prevNextStopIdxRef.current = effectiveNextStopIndex;
        ctx.lastStaticStateFingerprintRef.current = stateFp;
    } else if ( staticStateChanged ) {
        ctx.lastStaticStateFingerprintRef.current = stateFp;
    }

    if ( nextStopIndexChanged || staticStateChanged ) {
        syncRealtimeTransitPathStopLabels(
            ctx.map,
            ctx.stopLabelControllerRef,
            pastStops,
            remainingStops,
            shapePoints,
            ctx.colorHex,
            ctx.isDarkUi,
            delay,
        );
    }

    return true;
}

/**
 * Refreshes traversed and current segment polylines from live vehicle position.
 *
 * @param map MapLibre map instance.
 * @param refs Shared controller refs.
 * @param vehicleManager Vehicle position resolver.
 */
export function refreshRealtimeTransitCurrentSegment(
    map: maplibregl.Map,
    refs: RealtimeTransitPathControllerRefs,
    vehicleManager: VehicleManager,
): void {
    const shapePoints = refs.shapePointsRef.current;
    const shapeStartIdx = refs.shapeStartIdxRef.current;
    const nextStopIndex = refs.nextStopIndexRef.current;
    const nextStopShapeIdx = refs.nextStopShapeIdxRef.current;
    const previousStopShapeIdx = refs.previousStopShapeIdxRef.current;
    const tripId = refs.tripIdRef.current;
    const lineColorHex = refs.colorHexRef.current;

    if ( shapePoints === null || shapePoints.length === 0 ) {
        return;
    }

    const nowMs = Date.now();
    const leadingCoord = resolveVehicleLeadingCoord( vehicleManager, tripId, nowMs );

    const snapIdx = leadingCoord !== null
        ? findNearestShapeIndex(
            shapePoints,
            leadingCoord,
            Math.max( 0, previousStopShapeIdx - REALTIME_TRANSIT_PATH_SHAPE_SNAP_BACKWARD_TOLERANCE_POINTS ),
            nextStopShapeIdx,
        )
        : previousStopShapeIdx;

    if ( nextStopIndex > 0 && snapIdx > shapeStartIdx ) {
        const pastView = shapePoints.slice( shapeStartIdx, snapIdx + 1 );
        const pastCoords = shapeViewToCoordinates( pastView );

        if ( pastCoords.length >= 2 ) {
            updateGeoJsonLineData(
                map,
                REALTIME_TRANSIT_PATH_TRAVERSED_SOURCE_ID,
                REALTIME_TRANSIT_PATH_TRAVERSED_LAYER_ID,
                pastCoords,
                lineColorHex,
                REALTIME_TRANSIT_PATH_LINE_STYLE_TRAVERSED,
                REALTIME_TRANSIT_PATH_REMAINING_LAYER_ID,
            );
        }
    } else {
        removeLayerAndSource(
            map,
            REALTIME_TRANSIT_PATH_TRAVERSED_LAYER_ID,
            REALTIME_TRANSIT_PATH_TRAVERSED_SOURCE_ID,
        );
    }

    const segView = shapePoints.slice( snapIdx, nextStopShapeIdx );
    const segCoords = shapeViewToCoordinates( segView );

    const coordinates: [number, number][] = leadingCoord !== null
        ? [ leadingCoord, ...segCoords ]
        : segCoords;

    if ( coordinates.length < 2 ) {
        return;
    }

    updateGeoJsonLineData(
        map,
        REALTIME_TRANSIT_PATH_CURRENT_SOURCE_ID,
        REALTIME_TRANSIT_PATH_CURRENT_LAYER_ID,
        coordinates,
        lineColorHex,
        REALTIME_TRANSIT_PATH_LINE_STYLE_ACTIVE,
        VEHICLE_CUSTOM_LAYER_ID,
    );
}
